import React from 'react';
import { CheckCircle, ExternalLink } from 'lucide-react';
import { projects } from '../data/mockData';
import { Card, CardContent } from './ui/card';

const TrackRecord = () => {
  const categories = [...new Set(projects.map(p => p.category))];

  return (
    <section id="projects" className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-amber-400/10 border border-amber-400/20 rounded-full px-4 py-2 mb-4">
            <span className="text-amber-600 font-medium text-sm">Our Track Record</span>
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            Transformative Projects & Impact
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Delivering excellence across national development, infrastructure, and capacity building initiatives
          </p>
        </div>

        {/* Stats Banner */}
        <div className="bg-gradient-to-r from-blue-900 to-slate-900 rounded-3xl p-8 mb-12 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
          <div>
            <div className="text-4xl font-bold text-amber-400 mb-2">500+</div>
            <div className="text-gray-300 text-sm">Innovative Projects</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-amber-400 mb-2">134+</div>
            <div className="text-gray-300 text-sm">Public/Private Sector Projects</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-amber-400 mb-2">₦65B+</div>
            <div className="text-gray-300 text-sm">Sukuk Bonds Facilitated</div>
          </div>
          <div>
            <div className="text-4xl font-bold text-amber-400 mb-2">17+</div>
            <div className="text-gray-300 text-sm">Sectors Served</div>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mb-12">
          {projects.map((project) => (
            <Card
              key={project.id}
              className="group hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-amber-400 overflow-hidden"
            >
              <CardContent className="p-8">
                {/* Category Badge */}
                <div className="mb-4">
                  <span className="inline-block bg-gradient-to-r from-amber-400 to-yellow-600 text-slate-900 text-xs font-bold px-3 py-1 rounded-full">
                    {project.category}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-2xl font-bold text-slate-900 mb-3 group-hover:text-blue-900 transition-colors">
                  {project.title}
                </h3>

                {/* Description */}
                <p className="text-gray-600 mb-4 leading-relaxed">
                  {project.description}
                </p>

                {/* Client */}
                <div className="flex items-center space-x-2 text-sm">
                  <CheckCircle className="text-green-600 flex-shrink-0" size={18} />
                  <span className="text-gray-700">
                    <strong>Client:</strong> {project.client}
                  </span>
                </div>

                {/* Hover Link */}
                <div className="mt-4 pt-4 border-t border-gray-200 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <button className="flex items-center space-x-2 text-amber-600 hover:text-amber-700 font-semibold">
                    <span>View Case Study</span>
                    <ExternalLink size={16} />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Highlights */}
        <div className="bg-white rounded-3xl p-8 lg:p-12 border-2 border-gray-200">
          <h3 className="text-3xl font-bold text-slate-900 mb-8 text-center">
            Additional Project Highlights
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              "Business Leaders Forum (BLF) for Policy Development",
              "Free Carton Handbook Initiative (FCHI) for Youth Health",
              "Drive For Free Project for Driver Empowerment",
              "NAPWAP - AI-Powered Project Watch Portal",
              "Behavioral Assessment for Protocol Officers",
              "Global Communication Platform for Nigerian Diplomats",
              "Performance Improvement Templates for Employees",
              "Branding-Without-Borders Initiative",
              "MINDIVER Program for Solid Mineral Sector"
            ].map((highlight, index) => (
              <div
                key={index}
                className="flex items-start space-x-3 p-4 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl hover:shadow-md transition-shadow"
              >
                <CheckCircle className="text-amber-600 flex-shrink-0 mt-1" size={20} />
                <span className="text-gray-700 font-medium text-sm">{highlight}</span>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <p className="text-2xl text-slate-900 font-semibold mb-6">
            Ready to create your success story?
          </p>
          <button
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-amber-400 to-yellow-600 text-slate-900 hover:from-amber-500 hover:to-yellow-700 font-semibold px-10 py-4 rounded-lg transition-all duration-300 transform hover:scale-105 text-lg"
          >
            Start Your Project Today
          </button>
        </div>
      </div>
    </section>
  );
};

export default TrackRecord;
